/**
 * Runtime-API polyfills for old browsers (issue #975).
 *
 * Vite/esbuild down-level *syntax* for the browserslist targets but do NOT
 * shim missing *runtime APIs*, and neither `core-js` nor
 * `@vitejs/plugin-legacy` is installed. Two modern APIs therefore reach
 * Safari < 16.4 unshimmed and hard-crash the app:
 *
 *   - `Object.hasOwn`                                (Safari 15.4+; pulled in by
 *      a markdown-render dependency, mdast-util-gfm-footnote)
 *   - `CanvasRenderingContext2D.prototype.roundRect` (Safari 16.4+; used
 *      directly by the voice-message waveform renderer, waveform.tsx)
 *
 * This module is imported FIRST in `src/index.tsx`, before any app code, so the
 * shims are installed before the crashing code paths can run.
 *
 * The install functions use `??=`-style guards: a native implementation is
 * never overwritten. They are exported so unit tests can drive them against a
 * controlled target, and are also invoked once here for the import side effect.
 */

/**
 * `Object.hasOwn(obj, key)` — Safari 15.4+.
 * Falls back to `Object.prototype.hasOwnProperty` via `.call` so it works even
 * on null-prototype objects. Native impl is left untouched when present.
 */
export function installObjectHasOwn(): void {
  const O = Object as typeof Object & {
    hasOwn?: (o: object, k: PropertyKey) => boolean;
  };
  O.hasOwn ??= (obj: object, key: PropertyKey): boolean =>
    Object.prototype.hasOwnProperty.call(obj, key);
}

type Radii = number | DOMPointInit | (number | DOMPointInit)[];

interface Corner {
  x: number;
  y: number;
}

function toCorner(value: number | DOMPointInit): Corner {
  if (typeof value === "number") {
    return { x: value, y: value };
  }
  return { x: value.x ?? 0, y: value.y ?? 0 };
}

const clone = (c: Corner): Corner => ({ x: c.x, y: c.y });

/**
 * Expand the `radii` argument (scalar | DOMPointInit | array of 1–4 of either)
 * into the four per-corner radii, following the canvas spec's 1/2/3/4-element
 * expansion rules, and validate each (negative → RangeError).
 *
 * Returns four DISTINCT Corner objects (never shared references) so the caller
 * can mutate them independently while clamping.
 * @see https://html.spec.whatwg.org/multipage/canvas.html#dom-context-2d-roundrect
 */
function normalizeRadii(radii: Radii): [Corner, Corner, Corner, Corner] {
  const list = Array.isArray(radii) ? radii : [radii];

  if (list.length < 1 || list.length > 4) {
    throw new RangeError(
      `roundRect: radii must have 1 to 4 elements, got ${list.length}`,
    );
  }

  const p = list.map(toCorner);

  // Negative radii are a hard error; non-finite radii make the whole call a
  // no-op (signaled to the caller as null).
  for (const c of p) {
    if (c.x < 0 || c.y < 0) {
      throw new RangeError("roundRect: radii must be non-negative");
    }
  }

  let ul: Corner;
  let ur: Corner;
  let lr: Corner;
  let ll: Corner;

  // Each corner is a fresh clone so shared source entries (e.g. a single scalar
  // radius, or the 2/3-element forms) never alias — clamping must not scale the
  // same object more than once.
  switch (p.length) {
    case 1:
      ul = clone(p[0]);
      ur = clone(p[0]);
      lr = clone(p[0]);
      ll = clone(p[0]);
      break;
    case 2:
      ul = clone(p[0]);
      lr = clone(p[0]);
      ur = clone(p[1]);
      ll = clone(p[1]);
      break;
    case 3:
      ul = clone(p[0]);
      ur = clone(p[1]);
      ll = clone(p[1]);
      lr = clone(p[2]);
      break;
    default: // 4
      [ul, ur, lr, ll] = [clone(p[0]), clone(p[1]), clone(p[2]), clone(p[3])];
      break;
  }

  return [ul, ur, lr, ll];
}

/**
 * `CanvasRenderingContext2D.prototype.roundRect(x, y, w, h, radii?)` —
 * Safari 16.4+. Standard-compliant shim covering the
 * `number | DOMPointInit | (number | DOMPointInit)[]` radii forms, including
 * elliptical corners (drawn with `ellipse()`). Radii are uniformly scaled down
 * when a side is too short so opposing corners never overlap (spec step), and
 * negative width/height mirror the rectangle (spec behavior). Negative radii
 * throw RangeError; non-finite radii or geometry make the call a silent no-op
 * — all per spec.
 *
 * Like the native method, this adds to the current sub-path; it does not call
 * `beginPath()` or `fill()` — callers do that.
 *
 * Native impl is left untouched when present.
 *
 * @param ctor The `CanvasRenderingContext2D` constructor (injectable for tests).
 */
export function installRoundRect(
  ctor:
    | typeof CanvasRenderingContext2D
    | undefined = typeof CanvasRenderingContext2D !== "undefined"
    ? CanvasRenderingContext2D
    : undefined,
): void {
  // No canvas in this environment (e.g. SSR / worker) — nothing to shim.
  if (!ctor) {
    return;
  }

  const proto = ctor.prototype as CanvasRenderingContext2D & {
    roundRect?: (
      x: number,
      y: number,
      w: number,
      h: number,
      radii?: Radii,
    ) => void;
  };

  proto.roundRect ??= function roundRect(
    this: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    radii: Radii = 0,
  ): void {
    if (
      !Number.isFinite(x) ||
      !Number.isFinite(y) ||
      !Number.isFinite(w) ||
      !Number.isFinite(h)
    ) {
      // Per spec, non-finite geometry is a silent no-op.
      return;
    }

    let [ul, ur, lr, ll] = normalizeRadii(radii);

    // Per spec, a non-finite radius component makes the whole call a no-op
    // (checked after negative-radius validation, which throws).
    for (const c of [ul, ur, lr, ll]) {
      if (!Number.isFinite(c.x) || !Number.isFinite(c.y)) {
        return;
      }
    }

    // Negative width/height flip the rectangle. Normalize to a positive-extent
    // rectangle at (nx, ny) with size (absW, absH) and swap corners to match,
    // so all drawing below can assume a top-left origin (spec behavior).
    const absW = Math.abs(w);
    const absH = Math.abs(h);
    const nx = w < 0 ? x + w : x;
    const ny = h < 0 ? y + h : y;
    if (w < 0) {
      [ul, ur] = [ur, ul];
      [ll, lr] = [lr, ll];
    }
    if (h < 0) {
      [ul, ll] = [ll, ul];
      [ur, lr] = [lr, ur];
    }

    // Clamp: scale every radius by the smallest ratio so adjacent radii never
    // exceed the side they share (spec's uniform-scale step).
    const scales = [
      ul.x + ur.x > absW ? absW / (ul.x + ur.x) : 1, // top edge
      ur.y + lr.y > absH ? absH / (ur.y + lr.y) : 1, // right edge
      lr.x + ll.x > absW ? absW / (lr.x + ll.x) : 1, // bottom edge
      ll.y + ul.y > absH ? absH / (ll.y + ul.y) : 1, // left edge
    ];
    const scale = Math.min(1, ...scales);
    if (scale < 1) {
      for (const c of [ul, ur, lr, ll]) {
        c.x *= scale;
        c.y *= scale;
      }
    }

    // Trace clockwise from just after the upper-left corner over the normalized
    // (positive-extent) rectangle. Each corner is a quarter ellipse; ellipse()
    // supports the elliptical (rx != ry) case that arcTo cannot.
    const HALF_PI = Math.PI / 2;
    const right = nx + absW;
    const bottom = ny + absH;
    this.moveTo(nx + ul.x, ny);
    this.lineTo(right - ur.x, ny);
    this.ellipse(right - ur.x, ny + ur.y, ur.x, ur.y, 0, -HALF_PI, 0);
    this.lineTo(right, bottom - lr.y);
    this.ellipse(right - lr.x, bottom - lr.y, lr.x, lr.y, 0, 0, HALF_PI);
    this.lineTo(nx + ll.x, bottom);
    this.ellipse(nx + ll.x, bottom - ll.y, ll.x, ll.y, 0, HALF_PI, Math.PI);
    this.lineTo(nx, ny + ul.y);
    this.ellipse(nx + ul.x, ny + ul.y, ul.x, ul.y, 0, Math.PI, HALF_PI * 3);
    this.closePath();
    // Per spec, roundRect ends by starting a new subpath at the ORIGINAL
    // (x, y) — not the normalized origin used for drawing.
    this.moveTo(x, y);
  };
}

// Install on import for old browsers. `??=` guards keep native impls intact.
installObjectHasOwn();
installRoundRect();
